const KEYS = { best: "cave-critter-best", muted: "cave-critter-muted", dpad: "cave-critter-dpad" };

export const loadBest = () => Number(localStorage.getItem(KEYS.best) ?? 0) || 0;
export const saveBest = (v: number) => localStorage.setItem(KEYS.best, String(v));

export const loadMuted = () => localStorage.getItem(KEYS.muted) === "1";
export const saveMuted = (v: boolean) => localStorage.setItem(KEYS.muted, v ? "1" : "0");

export const isTouchDevice = () => {
  if (typeof window === "undefined") return false;
  const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  if (!hasTouch) return false;
  const fine = window.matchMedia?.("(pointer: fine) and (hover: hover)").matches;
  return !fine;
};

export const loadDpad = () => {
  const v = localStorage.getItem(KEYS.dpad);
  return v === null ? isTouchDevice() : v === "1";
};
export const saveDpad = (v: boolean) => localStorage.setItem(KEYS.dpad, v ? "1" : "0");
export type ControlMode = "dpad" | "joystick" | "swipe";
export const CONTROL_MODES: ControlMode[] = ["dpad", "joystick", "swipe"];
export const loadControls = (): ControlMode => {
  const v = localStorage.getItem("cave-critter-controls");
  if (v === "dpad" || v === "joystick" || v === "swipe") return v;
  return loadDpad() ? "dpad" : "swipe";
};
export const saveControls = (m: ControlMode) => { localStorage.setItem("cave-critter-controls", m); saveDpad(m !== "swipe"); };
export type StickSize = "s" | "m" | "l";
export const STICK_SIZES: StickSize[] = ["s", "m", "l"];
export const loadStickSize = (): StickSize => { const v = localStorage.getItem("cave-critter-stick"); return v === "s" || v === "l" ? v : "m"; };
export const saveStickSize = (v: StickSize) => localStorage.setItem("cave-critter-stick", v);

const LEVEL_BEST_KEY = "cave-critter-level-best";
export const loadLevelBests = (): Record<number, number> => {
  try { return JSON.parse(localStorage.getItem(LEVEL_BEST_KEY) ?? "{}"); } catch { return {}; }
};
export const saveLevelBest = (level: number, score: number) => {
  const all = loadLevelBests();
  if ((all[level] ?? -1) < score) { all[level] = score; localStorage.setItem(LEVEL_BEST_KEY, JSON.stringify(all)); }
};
export const starsFor = (score: number | undefined) => (score === undefined ? 0 : score >= 3500 ? 3 : score >= 2000 ? 2 : 1);

const UNLOCK_KEY = "cave-critter-unlocked";
export const loadUnlocked = () => Math.max(1, Number(localStorage.getItem(UNLOCK_KEY) ?? 1) || 1);
export const saveUnlocked = (level: number) => { if (level > loadUnlocked()) localStorage.setItem(UNLOCK_KEY, String(level)); };
export const SETBACK_LEVELS = 2;
const FLOOR_KEY = "cave-critter-retry-floor";
export const applySetback = (level: number) => {
  const floor = Math.max(1, Number(localStorage.getItem(FLOOR_KEY) ?? 1) || 1, level - SETBACK_LEVELS);
  localStorage.setItem(FLOOR_KEY, String(floor));
  const unlocked = Math.max(1, Math.min(loadUnlocked(), floor));
  localStorage.setItem(UNLOCK_KEY, String(unlocked));
  return unlocked;
};

export type BoardSize = "11" | "9" | "tall";
const BOARD_KEY = "cave-critter-board";
export const loadBoard = (): BoardSize => { const v = localStorage.getItem(BOARD_KEY); return v === "9" || v === "11" ? v : "tall"; };
export const saveBoard = (v: BoardSize) => localStorage.setItem(BOARD_KEY, v);

export type Platform = "desktop" | "mobile";
export const platformKey = (): Platform => (window.innerWidth >= 900 && !isTouchDevice() ? "desktop" : "mobile");
export const todayKey = () => new Date().toISOString().slice(0, 10);
export const shouldNudgeInstall = () => localStorage.getItem("cave-critter-installed") !== "1" && localStorage.getItem("cave-critter-install-nudge") !== todayKey();
export const markInstallNudged = () => localStorage.setItem("cave-critter-install-nudge", todayKey());
export const markInstalled = () => localStorage.setItem("cave-critter-installed", "1");

const SKIN_KEY = "cave-critter-skin";
export const loadSkin = (): string => localStorage.getItem(SKIN_KEY) ?? "red";
export const saveSkin = (v: string) => localStorage.setItem(SKIN_KEY, v);
export const levelStars = () => Object.values(loadLevelBests()).reduce((a, v) => a + starsFor(v), 0);
export const totalStars = () => levelStars() + streakBonusStars(loadStreak().best);

const STREAK_KEY = "cave-critter-daily-streak";
export const STREAK_REWARDS: { days: number; stars: number }[] = [{ days: 3, stars: 1 }, { days: 7, stars: 3 }, { days: 14, stars: 5 }, { days: 30, stars: 10 }];
export const streakBonusStars = (count: number) => STREAK_REWARDS.filter((r) => count >= r.days).reduce((a, r) => a + r.stars, 0);
export const nextStreakReward = (count: number) => STREAK_REWARDS.find((r) => count < r.days) ?? null;
export const loadStreak = (): { last: string; count: number; best: number } => {
  try { const v = JSON.parse(localStorage.getItem(STREAK_KEY) ?? "null"); if (v && typeof v.count === "number" && typeof v.last === "string") return { best: v.count, ...v }; } catch { /* ignore */ }
  return { last: "", count: 0, best: 0 };
};
export const currentStreak = (today = todayKey()) => {
  const s = loadStreak();
  const yesterday = new Date(Date.parse(today) - 86400000).toISOString().slice(0, 10);
  return s.last === today || s.last === yesterday ? s.count : 0;
};
export const recordDailyPlay = (today = todayKey()) => {
  const s = loadStreak();
  if (s.last === today) return s.count;
  const yesterday = new Date(Date.parse(today) - 86400000).toISOString().slice(0, 10);
  const count = s.last === yesterday ? s.count + 1 : 1;
  localStorage.setItem(STREAK_KEY, JSON.stringify({ last: today, count, best: Math.max(s.best, count) }));
  return count;
};

const PLAYER_KEY = "cave-critter-player-id";
export const playerId = () => {
  let v = localStorage.getItem(PLAYER_KEY);
  if (!v) { v = (crypto.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`); localStorage.setItem(PLAYER_KEY, v); }
  return v;
};
const PACK_KEY = "cave-critter-pack";
export const loadPackCode = () => localStorage.getItem(PACK_KEY);
export const savePackCode = (code: string | null) => { if (code) localStorage.setItem(PACK_KEY, code); else localStorage.removeItem(PACK_KEY); };
